import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import clsx from 'clsx';
import { FormValue, Type } from '../type';
import { useAddress } from '@/store/selector';
import { useValidate } from '../hooks/useValidate';
import { useCheckClosePair } from '@/hooks/useCheckClosePair';
import { usePairsByIndex } from '../hooks/usePairsByIndex';
import { usePriceByIndex } from '../hooks/usePriceByIndex';
import { OpenTrade } from '../open-trade';
import { formatDisplay } from '@/utils/format-display';

export function ButtonSubmit(props: { disabled: boolean; onClose: () => void }) {
    const { disabled, onClose } = props;
    const { control, setError, handleSubmit } = useFormContext<FormValue>();
    const address = useAddress();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [type, pairIndex] = useWatch({ control, name: ['type', 'pairIndex'] });
    const pair = usePairsByIndex(pairIndex);
    const price = usePriceByIndex(pairIndex);
    const { validateForm, errorOnchange } = useValidate();
    const { isClose } = useCheckClosePair(Number(pairIndex));

    const onSubmit = useCallback(() => {
        const validate = validateForm(type);
        if (validate) {
            return setError('root', { message: validate });
        }
        handleSubmit(
            OpenTrade({
                address,
                buy: type === Type.Long,
                onSuccess: () => {
                    onClose();
                    navigate('/orders', { state: { isOpened: true } });
                },
            })
        )();
    }, [address, handleSubmit, navigate, onClose, setError, type, validateForm]);

    return (
        <div className="grid grid-cols-3 gap-3 pt-2">
            <button
                onClick={onClose}
                className="col-span-1 py-4 rounded-xl bg-surface-light text-slate-900 font-bold text-base hover:bg-slate-200 active:scale-[0.98] transition-all"
            >
                {t('Cancel')}
            </button>
            <button
                disabled={disabled || isClose || !!errorOnchange}
                className={clsx(
                    'disabled:opacity-50 col-span-2 py-3 rounded-xl text-white font-bold text-base shadow-glow-green hover:brightness-105 active:scale-[0.98] transition-all flex flex-col items-center justify-center leading-tight',
                    {
                        'bg-green-500': type === Type.Long,
                        'bg-red-500': type === Type.Short,
                    }
                )}
                onClick={onSubmit}
            >
                <span className="text-lg">{type}</span>
                <span className="text-xs font-medium opacity-80 mt-0.5">
                    {pair?.symbol} @{' '}
                    <span className="text-sm font-bold">
                        {formatDisplay(price || 0)}
                    </span>
                </span>
            </button>
        </div>
    );
}
